
import React from 'react';
import { XIcon, ColorfulTikTokIcon, ColorfulInstagramIcon, SparklesIcon, ColorfulOcrIcon, ColorfulWhoisIcon, ColorfulYoutubeDownloaderIcon } from './Icons';

interface Change {
    title: string;
    description: string;
    icon: React.FC<{ className?: string }>;
}

interface ChangelogEntry {
    date: string;
    version: string;
    changes: Change[];
}

const CHANGELOG_DATA: ChangelogEntry[] = [
    {
        date: 'October 28, 2025',
        version: 'v2.2.0',
        changes: [
            {
                title: 'New: Video Downloader Suite',
                description: 'Added a suite of new tools to download videos from YouTube, Instagram, Pinterest, TikTok, and Twitter (X). The Facebook downloader has also been improved and moved to this new category.',
                icon: ColorfulYoutubeDownloaderIcon,
            },
            {
                title: 'New: WHOIS Lookup Tool',
                description: 'A new tool to look up registration details for any domain name, including registrar information, creation and expiration dates.',
                icon: ColorfulWhoisIcon,
            },
        ],
    },
    {
        date: 'October 26, 2025',
        version: 'v2.1.0',
        changes: [
            {
                title: 'New Tool: TikTok Downloader',
                description: 'We\'ve added a new tool to download TikTok videos, often without the watermark. Just paste the URL and get the video file directly.',
                icon: ColorfulTikTokIcon,
            },
            {
                title: 'Instagram Downloader Upgraded',
                description: 'The Instagram downloader is now more powerful. It uses multiple strategies to find videos, offers HD and SD quality options, and is more reliable thanks to an expanded list of proxies.',
                icon: ColorfulInstagramIcon,
            },
        ],
    },
    {
        date: 'October 18, 2025',
        version: 'v2.0.0',
        changes: [
            {
                title: 'Introducing the Image Editor',
                description: 'Go beyond documents with our brand new Image Editor. Crop, apply filters, add text, draw, and even perform AI-powered edits on any image.',
                icon: SparklesIcon,
            },
            {
                title: 'OCR for PDFs and Images',
                description: 'You can now extract text from scanned PDFs and images using our new Optical Character Recognition (OCR) tools, available in both "PDF to JPG" and "Image to Text".',
                icon: ColorfulOcrIcon,
            },
        ],
    },
];

export const Changelog: React.FC<{
    isOpen: boolean;
    onClose: () => void;
}> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 animate-fade-in-up" 
            style={{ animationDuration: '0.3s' }}
            onClick={onClose}
        >
            <div 
                className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-800/80 backdrop-blur-sm rounded-t-2xl">
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">What's New</h2>
                    <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 dark:bg-slate-700 transition-colors">
                        <XIcon className="w-5 h-5 text-slate-600 dark:text-slate-400"/>
                    </button>
                </header>
                <main className="p-4 sm:p-6 overflow-y-auto">
                    <div className="space-y-8">
                        {CHANGELOG_DATA.map((entry) => (
                            <div key={entry.date}>
                                <div className="flex items-center gap-4">
                                    <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">{entry.date}</h3>
                                    <span className="text-xs font-bold text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-full">{entry.version}</span>
                                </div>
                                <ul className="mt-4 space-y-4 border-l-2 border-slate-200 dark:border-slate-700 ml-3 pl-8">
                                    {entry.changes.map((change) => (
                                        <li key={change.title} className="relative">
                                            <div className="absolute -left-[38px] top-1 bg-white dark:bg-slate-800 p-1 rounded-full border-2 border-slate-200 dark:border-slate-700">
                                                <change.icon className="w-6 h-6" />
                                            </div>
                                            <h4 className="font-semibold text-slate-800 dark:text-slate-100">{change.title}</h4>
                                            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{change.description}</p>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </main>
            </div>
        </div>
    );
};